/**
 * The palm pipeline, end to end
 *
 *   image → normalise → grey-world → landmarks (ML, else classical) → palm frame
 *         → luminance → CLAHE → bilateral → vesselness → threshold → skeleton
 *         → trace → classify → mounts, hand type → features → reader
 *
 * DOM-free: the input is `{ width, height, data: Uint8ClampedArray (RGBA) }`, so
 * the same function runs in the page, in a Worker (pipeline.worker.js) and in the
 * test suite. **Nothing leaves the device** — there is no network call anywhere in
 * `src/hasta/`.
 *
 * ⚠ The pipeline refuses rather than guesses. If the hand cannot be segmented, or
 * the fingers cannot be found, or the palm is too small in the frame, it returns
 * `{ ok: false, stage, reason }` and no lines at all. A specific reason is the
 * product; a confident wrong trace is the failure.
 *
 * © Manish Jagdish Thatte
 */

import { normalise, greyWorld, toLuminance, clahe, bilateral } from './preprocessor.js';
import { vesselness } from './ridge-filter.js';
import { ridgeThreshold } from './edge-detector.js';
import { zhangSuen, prune, bridgeGaps } from './skeletonize.js';
import { trace, mergeStraight, splitAtCorners, simplify, measure, lengthOf } from './polyline.js';
import { palmFrame, toPalmSpace, erode } from './hand-geometry.js';
import { classify, absentLines, LINE_TYPES } from './line-classifier.js';
import { rankMounts } from './mounts.js';
import { handType } from './hand-shape.js';
import { digitPrints } from './fingerprints.js';
import { dateScale } from './system-of-seven.js';
import { getLandmarks } from './landmark-provider.js';
import { read } from './reader.js';
import { makeBinary, stats } from './image.js';

export const MIN_PALM_WIDTH_PX = 70;      // at the working size
export const MIN_LINE_LENGTH = 0.16;       // palm widths

/** Human-readable refusal reasons → i18n keys `palm.refusal.<reason>`. */
export const REFUSALS = Object.freeze(['no-skin', 'too-much-skin', 'fragmented', 'fingers-not-found', 'no-palm', 'palm-too-small', 'hand-cropped', 'no-lines']);

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** Binary mask of pixels inside the palm quadrilateral (palm space) and the hand, shrunk to keep the outline out. */
function palmMask(handMask, frame, shrink) {
  const { width: w, height: h } = handMask;
  const m = makeBinary(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!handMask.data[y * w + x]) continue;
    const { u, v } = toPalmSpace([x, y], frame);
    if (u > -0.02 && u < 1.04 && v > 0.0 && v < 1.0) m.data[y * w + x] = 1;
  }
  return shrink > 0 ? erode(m, shrink) : m;
}

/** Vesselness contrast scale from the image itself, with a floor so a flat photo does not turn noise into lines. */
function contrastScale(gray, mask) {
  const vals = []; for (let i = 0; i < gray.data.length; i += 3) if (mask.data[i]) vals.push(gray.data[i]);
  const { std } = stats(Float32Array.from(vals.length ? vals : [0, 0]));
  return Math.max(0.03, 0.35 * std);
}

/**
 * Detect and classify the lines inside the palm.
 * Returns `{ polylines, vesselness, skeleton }` where polylines are `{ points, closed, metrics }`.
 */
export function detectLines(gray, frame, handMask, { minLength = MIN_LINE_LENGTH } = {}) {
  const shrink = Math.max(3, Math.round(frame.width * 0.03));
  const pm = palmMask(handMask, frame, shrink);
  const c = contrastScale(gray, pm);
  const scales = [Math.max(1.5, frame.width * 0.012), Math.max(2.2, frame.width * 0.02), Math.max(3.2, frame.width * 0.03)];
  const ves = vesselness(gray, scales, 0.5, c);
  for (let i = 0; i < ves.data.length; i++) if (!pm.data[i]) ves.data[i] = 0;
  const { edges } = ridgeThreshold(ves);
  let skel = zhangSuen(edges);
  skel = prune(skel, Math.max(6, Math.round(frame.width * 0.06)));
  skel = bridgeGaps(skel, Math.max(4, Math.round(frame.width * 0.05)));
  skel = zhangSuen(skel);
  const merged = mergeStraight(trace(skel), { radius: 3, maxDot: -0.9 });
  const polylines = merged
    .flatMap((p) => splitAtCorners(p))
    .map((p) => ({ points: simplify(p.points, 1.5), closed: p.closed }))
    .filter((p) => p.points.length >= 2 && lengthOf(p.points) / frame.width >= minLength)
    .map((p) => ({ ...p, metrics: measure(p, gray) }));
  return { polylines, vesselness: ves, skeleton: skel, threshold: c };
}

/** Palm-space distance between two [u,v] points. */
const dPalm = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

/**
 * Turn classified lines, mounts and hand type into the flat feature set the reader
 * consumes. `confidence` groups are the assignment confidences (lines), a capped
 * shading-based figure (mounts), and the hand-type confidence.
 */
export function buildFeatures({ assignments, polylines, frame, mounts, hand }) {
  const values = {}; const confidence = {}; const notDetected = {};
  const byType = {};
  for (const a of assignments) if (!byType[a.type] || a.confidence > byType[a.type].confidence) byType[a.type] = a;
  for (const t of LINE_TYPES) {
    const a = byType[t.id];
    if (!a) { notDetected[t.id] = true; continue; }
    const f = a.features ?? null;
    values[`line.${t.id}.present`] = true;
    confidence[`line.${t.id}`] = a.confidence;
    if (!f) continue;
    if (t.id === 'heart') {
      const u = f.thumbEnd[0];
      if (u < 0.45) values['line.heart.origin'] = u < 0.27 ? 'jupiter' : u < 0.36 ? 'between' : 'saturn';
      values['line.heart.acrossPalm'] = f.thumbEnd[0] < 0.15 && f.ulnarEnd[0] > 0.9;
    }
    if (t.id === 'head') {
      const drop = f.thumbEnd[1] - f.ulnarEnd[1]; const run = Math.max(0.05, f.ulnarEnd[0] - f.thumbEnd[0]);
      const r = drop / run;
      values['line.head.slope'] = r < 0.12 ? 'straight' : r < 0.4 ? 'slight' : 'steep';
    }
    if (t.id === 'life') {
      const depth = polylines[a.index]?.metrics?.depth;
      values['line.life.steady'] = f.length >= 0.5 && (depth == null || depth >= 0.02);
    }
    if (t.id === 'fate') values['line.fate.origin'] = f.lowEnd[1] < 0.15 ? 'wrist' : 'higher';
  }
  if (byType.head && byType.life) {
    const d = dPalm(byType.head.features.thumbEnd, byType.life.features.highEnd);
    values['lines.headLife.separation'] = d < 0.06 ? 'linked' : d < 0.16 ? 'medium' : 'wide';
    confidence['lines.headLife'] = Math.min(byType.head.confidence, byType.life.confidence);
  }
  // mounts: a relative, shading-based ranking; confidence is deliberately low and capped.
  if (mounts?.length) {
    const top = mounts[0].relative;
    confidence.mount = top >= 1.35 ? Math.min(0.5, 0.15 + 0.2 * (top - 1)) : 0.1;
    for (const m of mounts) values[`mount.${m.id}.prominent`] = m.rank <= 2 && m.relative >= 1.35;
  }
  if (hand) { values['hand.type'] = hand.type; confidence.hand = hand.confidence; }
  return { values, confidence, notDetected };
}

/** Date scales (System of Seven) for the fate and life lines, anchored on the traced head/heart lines where present. */
export function dateScales(assignments, polylines, frame) {
  const out = {};
  const by = Object.fromEntries(assignments.map((a) => [a.type, a]));
  const anchors = {};
  if (by.head) anchors.head = by.head.features.pts.map((p) => p[1]).reduce((a, b) => a + b, 0) / by.head.features.pts.length;
  if (by.heart) anchors.heart = by.heart.features.pts.map((p) => p[1]).reduce((a, b) => a + b, 0) / by.heart.features.pts.length;
  if (by.fate) out.fate = dateScale(polylines[by.fate.index].points, 'fate', frame, { anchors });
  if (by.life) out.life = dateScale(polylines[by.life.index].points, 'life', frame);
  return out;
}

/**
 * Everything downstream of the traced lines. Called by `analyse()` and again, with
 * edited polylines / assignments, whenever the user corrects a line.
 */
export function interpret({ frame, polylines, assignments, mounts, hand, locale = 'en' }) {
  const features = buildFeatures({ assignments, polylines, frame, mounts, hand });
  return { features, reading: read(features, { locale }), dates: dateScales(assignments, polylines, frame) };
}

/**
 * Run the whole pipeline on an RGBA image.
 * `opts.landmarks` may supply externally computed landmarks; `opts.locale` picks reading text.
 */
export async function analyse(img, opts = {}) {
  const t0 = now(); const timings = {};
  const lap = (k, t) => { timings[k] = Math.round(now() - t); return now(); };
  let t = now();

  const norm = normalise(img, { maxEdge: opts.maxEdge });
  const balanced = greyWorld(norm);
  t = lap('preprocess', t);

  const found = opts.landmarks ? { landmarks: opts.landmarks, mask: opts.mask, source: 'external' } : await getLandmarks(norm, { balanced, prefer: opts.prefer ?? 'ml' });
  t = lap('landmarks', t);
  const refuse = (stage, reason, extra = {}) => ({ ok: false, stage, reason, timings, ...extra });
  if (!found.landmarks) return refuse(found.stage ?? 'landmarks', found.reason ?? 'fingers-not-found', { segmentation: found.segmentation });
  const lm = found.landmarks; const mask = found.mask;
  if (!mask) return refuse('segmentation', 'no-skin');

  const frame = palmFrame(lm);
  if (!frame) return refuse('landmarks', 'no-palm');
  if (frame.width < MIN_PALM_WIDTH_PX) return refuse('quality', 'palm-too-small', { palmWidth: Math.round(frame.width) });
  if (found.segmentation && found.segmentation.borderShare > 0.5) return refuse('quality', 'hand-cropped');

  const gray = toLuminance(balanced);
  const eq = clahe(gray, 8, 3);
  const smooth = bilateral(eq, 2, 0.08);
  t = lap('tone', t);

  const { polylines, vesselness: ves, skeleton, threshold } = detectLines(smooth, frame, mask, { minLength: opts.minLength });
  t = lap('lines', t);
  if (!polylines.length) return refuse('lines', 'no-lines', { frame, landmarks: lm });

  const { assignments, unclassified } = classify(polylines, frame);
  const mounts = rankMounts(gray, frame);
  const hand = handType(lm, mask);
  const prints = digitPrints(gray, lm, { scale: norm.scale ?? 1 });
  t = lap('classify', t);

  const out = interpret({ frame, polylines, assignments, mounts, hand, locale: opts.locale });
  timings.total = Math.round(now() - t0);

  return {
    ok: true, stage: 'done', source: found.source, fellBack: !!found.fellBack, fallbackReason: found.fallbackReason ?? null,
    width: norm.width, height: norm.height, scale: norm.scale ?? 1,
    frame, landmarks: lm, polylines, assignments, unclassified, absent: absentLines(assignments),
    mounts, mountsMeta: { basis: mounts.basis, absolute: mounts.absolute }, hand, prints, ...out, timings,
    quality: {
      skinFraction: found.segmentation?.fraction ?? null, palmWidthPx: Math.round(frame.width),
      handedness: frame.handedness, landmarkConfidence: lm.confidence ?? null, lineCount: polylines.length,
      contrastScale: threshold
    },
    debug: opts.debug ? { gray, smooth, vesselness: ves, skeleton, mask } : null
  };
}
